import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../../ui/dialog';
import { Badge } from '../../../ui/badge';
import { BsArrowUpRightCircle } from 'react-icons/bs';
import { getRootTreeUrl } from '../../../../utils/navigation';
import { GitFork } from 'lucide-react';
import { Button } from '../../../ui/button';
import { useState } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../ui/tooltip';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../../../api';
import { useAttributionForAddress } from '../../../../hooks/attributions/useAttributionForAddress';
import { useAddressAndChainParams } from '../../../../hooks/routing/useAddressAndChainParams';

type RootModalProps = {
  address: string;
};

export const RootModal = ({ address }: RootModalProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { chain } = useAddressAndChainParams();

  // Fetch root address data only when modal is opened
  const { data: rootData } = useQuery({
    queryKey: ['rootAddress', address],
    queryFn: () => api.blockchain.findRoot(address),
    enabled: isOpen && !!address, // Only fetch when modal is opened and address exists
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Use the attribution hook to get root entity
  const { attribution: rootAttribution } = useAttributionForAddress(
    chain,
    rootData,
  );

  const rootEntity = rootAttribution?.entity;

  // Don't show the button if there's no address
  if (!address) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <GitFork className="h-4 w-4 text-gray-600" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Root Address Information</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          {rootData ? (
            <>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-muted-foreground">
                  Root Address:
                </span>
                <Badge
                  variant="outline"
                  className="border-blue-200 bg-blue-50 px-3 py-1 text-blue-700 hover:bg-blue-100 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-300"
                >
                  {rootData}
                </Badge>
              </div>
              {rootEntity && (
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-muted-foreground">
                    Entity:
                  </span>
                  <Badge
                    variant="outline"
                    className="border-blue-200 bg-blue-50 px-3 py-1 text-blue-700 hover:bg-blue-100 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-300"
                  >
                    {rootEntity}
                  </Badge>
                </div>
              )}
              <div className="flex items-center gap-2">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <a
                        href={getRootTreeUrl(rootData)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-blue-500 hover:text-blue-700"
                      >
                        <BsArrowUpRightCircle className="h-4 w-4" />
                        <span>View root address tree</span>
                      </a>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Opens in a new tab</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center py-8">
              <span className="text-sm text-muted-foreground">
                Loading root address...
              </span>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
